import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { headData } from "./statementData";

import {
  FtTable,
  SortType,
  FtTableHeadData,
} from "components/common/table/FtTable";

import {
  createFilterProps as baseCreateFilterProps,
  getDefaultInputValue,
  renderConditionalProp,
} from "utility";
import { EXTENDED_VIEW_WIDTH, MOBILE_WIDTH } from "constants/common";
import * as requestUrl from "constants/request";
import { extractError } from "utility/utils";
import { useScreenWidth } from "hooks/useScreenWidth";

import { useRootDispatch, useRootSelector } from "store/hooks";
import { useRequestDispatch } from "store/requestHooks";
import { useAddNotification, useRequest } from "hooks";
import { selectedAccountInfo } from "store/account/account-selector";

import {
  FetchStatements,
  fetchStatements,
} from "store/statements/statements-action";
import { selectAllStatments } from "store/statements/statements-slice";
import { FilterProps } from "store/common";
import { StatementData } from "store/statements/statements-state";
import { setFilters } from "store/statements/statements-slice";

import classes from "./statementTable.module.scss";
import {
  DEFAULT_OCTET_STREAM,
  getExtension,
  getExtensionFromContentType,
} from "utility/mimeTypes";

import { useInput, UseInput } from "hooks/useInput";
import { StatementPreviewDialog } from "./Dialog/StatementPreviewDialog";

interface StatementTableProps {
  hideControls?: boolean;
  hideHeader?: boolean;
}

interface SaveFilePickerWritable {
  close: () => Promise<void>;
  write: (data: Blob) => Promise<void>;
}

interface SaveFilePickerHandle {
  createWritable: () => Promise<SaveFilePickerWritable>;
}

interface SaveFilePickerWindow extends Window {
  showSaveFilePicker?: (options?: {
    suggestedName?: string;
    types?: Array<{
      accept: Record<string, string[]>;
      description?: string;
    }>;
  }) => Promise<SaveFilePickerHandle>;
}

const PAGE_SIZE = 10;

const isSaveDialogCancelled = (error: unknown): boolean => {
  return error instanceof DOMException && error.name === "AbortError";
};

const saveBlobToDisk = async (
  blob: Blob,
  fileName: string,
  contentType: string,
) => {
  const browserWindow = window as SaveFilePickerWindow;
  const dotExtension = getExtensionFromContentType(contentType);

  if (browserWindow.showSaveFilePicker) {
    const fileHandle = await browserWindow.showSaveFilePicker({
      suggestedName: fileName,
      types: [
        {
          accept: {
            [contentType]: dotExtension ? [dotExtension] : [],
          },
          description: "Statement file",
        },
      ],
    });
    const writable = await fileHandle.createWritable();
    await writable.write(blob);
    await writable.close();
    return;
  }

  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(downloadUrl);
};

export const StatementTable: React.FC<StatementTableProps> = ({
  hideControls,
  hideHeader,
}) => {
  const dispatch = useRequestDispatch();
  const { request } = useRequest();
  const rootDispatch = useRootDispatch();
  const addNotification = useAddNotification();
  const [currentPage, setCurrentPage] = useState(1);
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [previewStatement, setPreviewStatement] =
    useState<StatementData | null>(null);
  const lastFetchedYear = useRef<string>("");
  const statementYear: UseInput = useInput(
    getDefaultInputValue(new Date().getFullYear().toString()),
  );

  const { t } = useTranslation([
    "common",
    "dashboard",
    "paymentactivity",
    "eStatements",
  ]);
  const width = useScreenWidth();
  const expandedView = width <= EXTENDED_VIEW_WIDTH;
  const mobileView = width <= MOBILE_WIDTH;

  const { filters, meta } = useRootSelector((state) => state.statements);
  const accountInfo = useRootSelector(selectedAccountInfo);

  const statements = useRootSelector(selectAllStatments);
  const currentYear = new Date().getFullYear().toString();

  const createFilterProps = useCallback(
    (filter: FilterProps): FilterProps => ({
      ...baseCreateFilterProps(filter),
      pageSize: filter?.pageSize ?? PAGE_SIZE,
      accountInformationId: filter?.accountInformationId ?? accountInfo?.id,
      transactionYear: filter?.transactionYear ?? currentYear,
    }),
    [accountInfo?.id, currentYear],
  );

  const currentSortColumn = "created";
  const currentSortDir: SortType = "desc";

  const sortedStatements = useMemo(() => {
    return [...statements].sort((a, b) => {
      const first = new Date(a?.created ?? 0).getTime();
      const second = new Date(b?.created ?? 0).getTime();
      return second - first;
    });
  }, [statements]);

  const pagedStatements = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return sortedStatements.slice(startIndex, startIndex + PAGE_SIZE);
  }, [currentPage, sortedStatements]);

  const tableRows = useMemo(
    () =>
      pagedStatements.map((statement: any) => ({
        ...statement,
        id: statement.fileId,
      })),
    [pagedStatements],
  );

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const handleDownload = useCallback(async () => {
    const fileIds = Array.from(selectedIds);
    if (fileIds.length === 0 || isDownloading) {
      return;
    }

    setIsDownloading(true);

    try {
      const statementsUrl = new URL(
        `${requestUrl.FT_STATEMENTS}/download`,
        requestUrl.FT_REQUEST_BASE_URL,
      );
      const response = await request.post<Blob>(
        statementsUrl.toString(),
        { accountInformationId: accountInfo?.id, filePaths: fileIds },
        { responseType: "blob" },
      );
      const singleStatement = statements.find(
        (statement) => statement.path === fileIds[0],
      );

      const createdDate =
        fileIds.length === 1 ? singleStatement?.created?.split("T")[0] : "";
      const contentType =
        (response.headers?.["content-type"] as string | undefined) ??
        DEFAULT_OCTET_STREAM;
      const nameExt = getExtension(singleStatement?.name ?? "");
      const extension =
        fileIds.length === 1
          ? (getExtensionFromContentType(contentType) ??
            (nameExt ? `.${nameExt}` : ".pdf"))
          : ".zip";
      const fileName =
        fileIds.length === 1
          ? `${accountInfo?.id}_${createdDate}${extension}`
          : `${accountInfo?.id}_statements${extension}`;

      await saveBlobToDisk(response.data, fileName, contentType);
      clearSelection();
    } catch (error) {
      if (isSaveDialogCancelled(error)) {
        return;
      }
      addNotification({
        messages: extractError(error),
        type: "ERROR",
        closable: true,
      });
    } finally {
      setIsDownloading(false);
    }
  }, [
    accountInfo?.id,
    addNotification,
    clearSelection,
    isDownloading,
    request,
    selectedIds,
    statements,
  ]);
  const handlePreview = useCallback((statement: StatementData) => {
    setPreviewStatement(statement);
  }, []);

  const closePreview = useCallback(() => {
    setPreviewStatement(null);
  }, []);

  const getLabelTranslation = (id: string) => t(`eStatements:${id}`);

  const currentPageKeys = useMemo(
    () => tableRows.map((statement: any) => statement.path as string),
    [tableRows],
  );

  const allSelected =
    currentPageKeys.length > 0 &&
    currentPageKeys.every((key) => selectedIds.has(key));

  const someSelected =
    currentPageKeys.some((key) => selectedIds.has(key)) && !allSelected;

  const toggleSelectCurrentPage = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);

      if (allSelected) {
        currentPageKeys.forEach((key) => next.delete(key));
      } else {
        currentPageKeys.forEach((key) => next.add(key));
      }

      return next;
    });
  }, [allSelected, currentPageKeys]);

  const computedHeadData = useMemo((): FtTableHeadData[] => {
    const checkboxColumn: FtTableHeadData = {
      id: "select",
      label: "",
      alwaysShow: true,
      width: 50,
      dynamicRender: ({ data }) => (
        <input
          type="checkbox"
          className={classes.RowCheckbox}
          checked={selectedIds.has(data.path)}
          onChange={() => toggleSelect(data.path)}
          onClick={(e) => e.stopPropagation()}
          aria-label={t("eStatements:select_statement")}
        />
      ),
    };

    const translatedData = headData.map((col) => {
      const transformed: FtTableHeadData = {
        ...col,
        label: col.label || getLabelTranslation(col.id),
      };

      if (expandedView && col.id === "fileTitle") {
        transformed.alwaysShow = false;
      }

      if (expandedView && col.id === "created") {
        transformed.width = undefined;
      }

      return transformed;
    });

    const previewColumn: FtTableHeadData = {
      id: "preview",
      label: "",
      alwaysShow: true,
      width: expandedView ? 40 : undefined,
      cellClassName: classes.PreviewCell,
      align: "right",
      dynamicRender: ({ data }) => (
        <ft-icon
          class={classes.PreviewIcon}
          role="button"
          aria-label={t("eStatements:preview_statement")}
          onClick={(e: Event) => {
            e.stopPropagation();
            handlePreview(data);
          }}
        >
          find_in_page
        </ft-icon>
      ),
    };

    checkboxColumn.label = (
      <input
        type="checkbox"
        className={classes.RowCheckbox}
        checked={allSelected}
        ref={(el) => {
          if (el) {
            el.indeterminate = someSelected;
          }
        }}
        onChange={toggleSelectCurrentPage}
        onClick={(e) => e.stopPropagation()}
        aria-label={t("eStatements:select_all_current_page")}
      />
    ) as unknown as string;

    return [checkboxColumn, ...translatedData, previewColumn];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedIds,
    toggleSelect,
    handlePreview,
    expandedView,
    allSelected,
    someSelected,
    toggleSelectCurrentPage,
    t,
  ]);

  useEffect(() => {
    if (accountInfo?.id) {
      const params: FilterProps = {
        sortColumn: 1,
        sortDir: 2,
        accountInformationId: accountInfo.id,
        transactionYear: currentYear,
      };
      rootDispatch(setFilters(params));
      lastFetchedYear.current = currentYear;
      dispatch<FetchStatements, FilterProps>(fetchStatements, params);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountInfo?.id, rootDispatch]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(statements.length / PAGE_SIZE));
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, statements.length]);

  const hasError = !meta.isLoading && !!meta.error;
  const isEmpty = !meta.isLoading && !meta.error && statements.length === 0;

  const rowClickhandler = () => {};

  const fetchStatementsWithYear = useCallback(
    (transactionYear: string) => {
      const statementFilters: FilterProps = {
        accountInformationId: accountInfo?.id,
        transactionYear,
        pageNumber: 1,
        pageSize: PAGE_SIZE,
      };
      clearSelection();
      setCurrentPage(1);
      rootDispatch(setFilters(statementFilters));
      lastFetchedYear.current = transactionYear;
      dispatch<FetchStatements, FilterProps>(fetchStatements, statementFilters);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accountInfo?.id, rootDispatch],
  );

  useEffect(() => {
    const currentSelect = statementYear.ref.current;
    const statementYearChangeHandler = (event: Event) => {
      statementYear.valueChangeHandler(event);
      const selectedYear = (event.target as HTMLInputElement).value;

      if (selectedYear && selectedYear !== lastFetchedYear.current) {
        fetchStatementsWithYear(selectedYear);
      }
    };

    if (currentSelect) {
      currentSelect.removeEventListener("selected", statementYearChangeHandler);
      currentSelect.addEventListener("selected", statementYearChangeHandler);
    }

    return () => {
      if (currentSelect) {
        currentSelect.removeEventListener(
          "selected",
          statementYearChangeHandler,
        );
      }
    };
  }, [fetchStatementsWithYear, statementYear]);

  return (
    <>
      <section className={classes.Wrapper}>
        {hideHeader ? null : (
          <div className={classes.Header}>
            <div className={classes.HeaderTitle}></div>
            <div className={classes.Filter}>
              <ft-select
                ref={statementYear.ref}
                class={[
                  "outline-button",
                  classes.WebFilterButton,
                  classes.yearSelect,
                ].join(" ")}
                aria-label="Statement year"
                value={statementYear.value}
                onBlur={statementYear.inputBlurHandler}
                outlined
              >
                {Array.from(
                  { length: 10 },
                  (_, i) => new Date().getFullYear() - i,
                ).map((year) => (
                  <ft-list-item
                    key={year}
                    value={year.toString()}
                    {...renderConditionalProp(
                      statementYear.value === year.toString(),
                      "selected",
                    )}
                  >
                    {year}
                  </ft-list-item>
                ))}
              </ft-select>
            </div>
          </div>
        )}
        {selectedIds.size > 0 && (
          <div
            className={classes.SelectionBar}
            role="toolbar"
            aria-label={t("eStatements:selection_toolbar")}
          >
            <ft-icon
              class={classes.ClearIcon}
              role="button"
              aria-label={t("eStatements:clear", "Clear selection")}
              onClick={clearSelection}
            >
              close
            </ft-icon>
            <span className={classes.SelectionCount}>
              {t("eStatements:files_selected", {
                count: selectedIds.size,
              })}
            </span>
            <ft-button
              class="outline-button"
              label={t("eStatements:download")}
              icon="download"
              trailingIcon
              disabled={isDownloading ? isDownloading : null}
              onClick={handleDownload}
            ></ft-button>
          </div>
        )}
        {hasError && (
          <div className={classes.ErrorMessage} role="alert">
            {t(
              "eStatements:load_error",
              "We couldn't load your statements. Try again.",
            )}
          </div>
        )}
        {isEmpty && (
          <div className={classes.ErrorMessage} role="status">
            {t("eStatements:no_results")}
          </div>
        )}
        {!hasError && !isEmpty && (
          <div className={[classes.TableWraper, "cursor-in-table"].join(" ")}>
            <FtTable
              tableHeight={
                mobileView
                  ? "calc(var(--appHeight) - 290px)"
                  : "calc(var(--appHeight) - 375px)"
              }
              dataAction={fetchStatements}
              headData={computedHeadData}
              tableData={tableRows}
              sortColumn={currentSortColumn}
              sortDir={currentSortDir}
              pageNum={currentPage}
              autoHeight={false}
              fillHeight={true}
              loading={meta?.isLoading}
              pagination={true}
              totalCount={sortedStatements.length}
              filters={filters}
              createFilterProps={createFilterProps}
              rowExpandedHeight={50}
              expandColumnWidth={42}
              expandedView={expandedView}
              showHeader={true}
              pageCount={PAGE_SIZE}
              paginationAction={setCurrentPage}
              rowClickhandler={rowClickhandler}
              rowClassName={(a: any) => {
                if (a && a.fileTitle) return "cursor-pointer";
              }}
              emptyMessage={t("eStatements:no_results")}
            />
          </div>
        )}
      </section>
      <StatementPreviewDialog
        accountInformationId={accountInfo?.id}
        isOpen={!!previewStatement}
        onClose={closePreview}
        statement={previewStatement}
      />
    </>
  );
};
